import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { SuggestionList } from "@/components/ui/suggestion-list";
import type {
  AddressResolutionCandidateSchema,
  AddressResolutionLookupResponseSchema,
} from "@/hera-api/client";
import {
  operatorAddressResolutionLookup,
  operatorAddressResolutionSave,
} from "@/hera-api/client";
import { usePlacesAutocomplete } from "@/hooks/use-places-autocomplete";
import { parseGoogleAddressComponents } from "@/lib/parse-google-address-components";
import { getQueryErrorMessage } from "@/lib/utils";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Search } from "lucide-react";
import { useState } from "react";

type SourceKey = "cache" | "geocodio" | "google" | "custom";

interface CustomResolution {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
  lat: string;
  lng: string;
}

const EMPTY_CUSTOM: CustomResolution = {
  address1: "",
  address2: "",
  city: "",
  state: "",
  zipcode: "",
  lat: "",
  lng: "",
};

function reconstructAddress(parts: {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
}): string {
  const street = [parts.address1?.trim(), parts.address2?.trim()]
    .filter(Boolean)
    .join(", ");
  const stateZip = [parts.state?.trim(), parts.zipcode?.trim()]
    .filter(Boolean)
    .join(" ");
  return [street, parts.city?.trim(), stateZip].filter(Boolean).join(", ");
}

function candidateToCustom(
  candidate: AddressResolutionCandidateSchema,
): CustomResolution {
  return {
    address1: candidate.address1 ?? "",
    address2: candidate.address2 ?? "",
    city: candidate.city ?? "",
    state: candidate.state ?? "",
    zipcode: candidate.zipcode ?? "",
    lat: candidate.lat != null ? String(candidate.lat) : "",
    lng: candidate.lng != null ? String(candidate.lng) : "",
  };
}

function CandidateCard({
  title,
  candidate,
  errorMessage,
  badges,
  selected,
  onSelect,
}: {
  title: string;
  candidate: AddressResolutionCandidateSchema | null | undefined;
  errorMessage?: string | null;
  badges?: React.ReactNode;
  selected: boolean;
  onSelect?: () => void;
}) {
  return (
    <button
      type="button"
      disabled={!candidate}
      onClick={onSelect}
      className={`w-full rounded-md border p-4 text-left transition-colors ${
        selected
          ? "border-primary bg-primary/5"
          : candidate
            ? "hover:bg-muted/50"
            : "opacity-70"
      }`}
      aria-pressed={selected}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-sm font-bold">{title}</span>
        {badges}
        {candidate?.matches_input === false ? (
          <Badge variant="destructive">Different property</Badge>
        ) : null}
        {selected ? <Badge>Selected</Badge> : null}
      </div>
      {candidate ? (
        <div className="space-y-1 text-sm">
          <div>{reconstructAddress(candidate) || "—"}</div>
          <div className="text-muted-foreground">
            Lat/Long:{" "}
            {candidate.lat != null && candidate.lng != null
              ? `${candidate.lat}, ${candidate.lng}`
              : "—"}
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {errorMessage || "No result."}
        </p>
      )}
    </button>
  );
}

export function AddressResolutionSetup() {
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipcode, setZipcode] = useState("");

  const [placesAutoCompleteInput, setPlacesAutoCompleteInput] = useState("");
  const { suggestions } = usePlacesAutocomplete(placesAutoCompleteInput, {
    includedRegionCodes: ["us"],
    includedPrimaryTypes: ["street_address"],
  });

  const [lookupResult, setLookupResult] =
    useState<AddressResolutionLookupResponseSchema | null>(null);
  const [selectedSource, setSelectedSource] = useState<SourceKey | null>(null);
  const [custom, setCustom] = useState<CustomResolution>(EMPTY_CUSTOM);

  const canLookup =
    address1.trim().length > 0 &&
    city.trim().length > 0 &&
    state.trim().length > 0;

  const lookupMutation = useMutation({
    mutationFn: async () => {
      const result = await operatorAddressResolutionLookup({
        body: {
          address1: address1.trim(),
          address2: address2.trim() || null,
          city: city.trim(),
          state: state.trim(),
          zipcode: zipcode.trim(),
        },
        throwOnError: true,
      });
      return result.data;
    },
    onSuccess: (data) => {
      setLookupResult(data);
      setSelectedSource(null);
      setCustom(EMPTY_CUSTOM);
    },
    onError: (error) => {
      toast({
        title: "Address lookup failed",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const lat = Number.parseFloat(custom.lat);
      const lng = Number.parseFloat(custom.lng);
      // Cache key = address entered at the top of the panel.
      // Geocoded columns = the selected/edited resolution components,
      // with formatted_address reconstructed from those same components.
      const geocodedAddress1 = custom.address1.trim() || null;
      const geocodedAddress2 = custom.address2.trim() || null;
      const geocodedCity = custom.city.trim() || null;
      const geocodedState = custom.state.trim() || null;
      const geocodedZipcode = custom.zipcode.trim() || null;
      const reconstructed = reconstructAddress({
        address1: geocodedAddress1,
        address2: geocodedAddress2,
        city: geocodedCity,
        state: geocodedState,
        zipcode: geocodedZipcode,
      });
      const result = await operatorAddressResolutionSave({
        body: {
          address1: address1.trim(),
          address2: address2.trim() || null,
          city: city.trim(),
          state: state.trim(),
          zipcode: zipcode.trim(),
          geocoded_formatted_address: reconstructed || null,
          geocoded_address1: geocodedAddress1,
          geocoded_address2: geocodedAddress2,
          geocoded_city: geocodedCity,
          geocoded_state: geocodedState,
          geocoded_zipcode: geocodedZipcode,
          geocoded_lat: lat,
          geocoded_lng: lng,
        },
        throwOnError: true,
      });
      return result.data;
    },
    onSuccess: () => {
      toast({
        title: "Address resolution saved",
        description:
          "The cached geocoding entry was updated and marked as a manual override.",
        variant: "secondary",
      });
      lookupMutation.mutate();
    },
    onError: (error) => {
      toast({
        title: "Failed to save address resolution",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const handleSuggestionClick = async (
    suggestion: google.maps.places.AutocompleteSuggestion,
  ) => {
    const place = suggestion.placePrediction?.toPlace();
    if (!place) return;
    await place.fetchFields({ fields: ["addressComponents"] });
    if (!place?.addressComponents) return;

    const parsed = parseGoogleAddressComponents(place.addressComponents);
    setAddress1(parsed.address1);
    setAddress2(parsed.address2);
    setCity(parsed.city);
    setState(parsed.state);
    setZipcode(parsed.zipcode);
  };

  const selectCandidate = (
    source: SourceKey,
    candidate: AddressResolutionCandidateSchema | null | undefined,
  ) => {
    if (!candidate) return;
    setSelectedSource(source);
    setCustom(candidateToCustom(candidate));
  };

  const latValid = !Number.isNaN(Number.parseFloat(custom.lat));
  const lngValid = !Number.isNaN(Number.parseFloat(custom.lng));
  const canSave = latValid && lngValid && !saveMutation.isPending;

  const updateCustom =
    (key: keyof CustomResolution) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCustom((previous) => ({ ...previous, [key]: event.target.value }));
      setSelectedSource("custom");
    };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Address Resolution</CardTitle>
          <CardDescription>
            Look up how an address resolves in the geocoding cache, on Geocodio,
            and on Google Maps. Select a result (or key in your own, including
            latitude/longitude) and save it to override the cached resolution.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="address-resolution-address1">Address Line 1</Label>
            <SuggestionList
              closeOnBlur
              suggestions={suggestions}
              renderSuggestion={(suggestion) => (
                <span>{suggestion.placePrediction?.text?.text}</span>
              )}
              getSuggestionKey={(suggestion) =>
                suggestion.placePrediction?.placeId ?? ""
              }
              onSuggestionSelect={handleSuggestionClick}
            >
              <Input
                id="address-resolution-address1"
                placeholder="Enter the street address"
                value={address1}
                onChange={(e) => {
                  setAddress1(e.target.value);
                  setPlacesAutoCompleteInput(e.target.value);
                }}
              />
            </SuggestionList>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="address-resolution-address2">
              Address Line 2 (Optional)
            </Label>
            <Input
              id="address-resolution-address2"
              placeholder="Apartment, suite, unit, etc."
              value={address2}
              onChange={(e) => setAddress2(e.target.value)}
            />
          </div>
          <div className="grid items-start gap-4 md:grid-cols-3">
            <div className="grid gap-2">
              <Label htmlFor="address-resolution-city">City</Label>
              <Input
                id="address-resolution-city"
                placeholder="Enter the city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="address-resolution-state">State</Label>
              <Input
                id="address-resolution-state"
                placeholder="XX"
                maxLength={2}
                value={state}
                onChange={(e) => setState(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="address-resolution-zipcode">ZIP Code</Label>
              <Input
                id="address-resolution-zipcode"
                placeholder="XXXXX"
                value={zipcode}
                onChange={(e) => setZipcode(e.target.value)}
              />
            </div>
          </div>
          <Button
            disabled={!canLookup || lookupMutation.isPending}
            onClick={() => lookupMutation.mutate()}
          >
            {lookupMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Search className="mr-2 h-4 w-4" />
            )}
            Lookup
          </Button>
        </CardContent>
      </Card>

      {lookupResult ? (
        <Card>
          <CardHeader>
            <CardTitle>Resolutions</CardTitle>
            <CardDescription>
              Pick the resolution to store in the geocoding cache, or adjust the
              fields below before saving.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 lg:grid-cols-3">
              <CandidateCard
                title="Cached entry"
                candidate={lookupResult.cache}
                errorMessage="No cache entry stored for this address."
                badges={
                  lookupResult.cache?.manual_override ? (
                    <Badge variant="secondary">Manual override</Badge>
                  ) : null
                }
                selected={selectedSource === "cache"}
                onSelect={() => selectCandidate("cache", lookupResult.cache)}
              />
              <CandidateCard
                title="Geocodio"
                candidate={lookupResult.geocodio}
                errorMessage={lookupResult.geocodio_error}
                selected={selectedSource === "geocodio"}
                onSelect={() =>
                  selectCandidate("geocodio", lookupResult.geocodio)
                }
              />
              <CandidateCard
                title="Google Maps"
                candidate={lookupResult.google}
                errorMessage={lookupResult.google_error}
                selected={selectedSource === "google"}
                onSelect={() => selectCandidate("google", lookupResult.google)}
              />
            </div>

            <div className="space-y-4 rounded-md border p-4">
              <p className="text-sm font-medium">
                Resolution
                {selectedSource === "custom" ? " (custom)" : ""}
              </p>
              <div className="grid items-start gap-4 md:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="address-resolution-save-address1">
                    Address Line 1
                  </Label>
                  <Input
                    id="address-resolution-save-address1"
                    value={custom.address1}
                    onChange={updateCustom("address1")}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="address-resolution-save-address2">
                    Address Line 2
                  </Label>
                  <Input
                    id="address-resolution-save-address2"
                    value={custom.address2}
                    onChange={updateCustom("address2")}
                  />
                </div>
              </div>
              <div className="grid items-start gap-4 md:grid-cols-3">
                <div className="grid gap-2">
                  <Label htmlFor="address-resolution-save-city">City</Label>
                  <Input
                    id="address-resolution-save-city"
                    value={custom.city}
                    onChange={updateCustom("city")}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="address-resolution-save-state">State</Label>
                  <Input
                    id="address-resolution-save-state"
                    maxLength={2}
                    value={custom.state}
                    onChange={updateCustom("state")}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="address-resolution-save-zipcode">
                    ZIP Code
                  </Label>
                  <Input
                    id="address-resolution-save-zipcode"
                    value={custom.zipcode}
                    onChange={updateCustom("zipcode")}
                  />
                </div>
              </div>
              <div className="grid items-start gap-4 md:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="address-resolution-save-lat">Latitude</Label>
                  <Input
                    id="address-resolution-save-lat"
                    placeholder="e.g. 39.752319"
                    value={custom.lat}
                    onChange={updateCustom("lat")}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="address-resolution-save-lng">Longitude</Label>
                  <Input
                    id="address-resolution-save-lng"
                    placeholder="e.g. -74.994810"
                    value={custom.lng}
                    onChange={updateCustom("lng")}
                  />
                </div>
              </div>
              {!latValid || !lngValid ? (
                <p className="text-sm text-muted-foreground">
                  Enter a numeric latitude and longitude to save.
                </p>
              ) : null}
              <Button disabled={!canSave} onClick={() => saveMutation.mutate()}>
                {saveMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Save to cache
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
